import { Coords, Size } from 'src/types';
import { CoordsUtils } from 'src/utils';

/**
 * Sorts coordinates by position (x and y)
 */
export const sortByPosition = (tiles: Coords[]) => {
    const xSorted = [...tiles];
    const ySorted = [...tiles];

    xSorted.sort((a, b) => a.x - b.x);
    ySorted.sort((a, b) => a.y - b.y);

    const highest = {
        byX: xSorted[xSorted.length - 1],
        byY: ySorted[ySorted.length - 1]
    };
    const lowest = {
        byX: xSorted[0],
        byY: ySorted[0]
    };

    const lowX = lowest.byX.x;
    const highX = highest.byX.x;
    const lowY = lowest.byY.y;
    const highY = highest.byY.y;

    return {
        byX: xSorted,
        byY: ySorted,
        highest,
        lowest,
        lowX,
        lowY,
        highX,
        highY
    };
};

/**
 * Returns a complete set of tiles that form a grid area
 * Takes in any number of tiles to use as points to encapsulate
 */
export const getGridSubset = (tiles: Coords[]): Coords[] => {
    const { lowX, lowY, highX, highY } = sortByPosition(tiles);

    const subset: Coords[] = [];

    for (let x = lowX; x < highX + 1; x += 1) {
        for (let y = lowY; y < highY + 1; y += 1) {
            subset.push({ x, y });
        }
    }

    return subset;
};

/**
 * Checks if a tile is within the given bounds
 */
export const isWithinBounds = (tile: Coords, bounds: Coords[]): boolean => {
    const { lowX, lowY, highX, highY } = sortByPosition(bounds);

    return (
        tile.x >= lowX &&
        tile.x <= highX &&
        tile.y >= lowY &&
        tile.y <= highY
    );
};

/**
 * Returns the four corners of a grid that encapsulates all tiles passed in
 * @param tiles - At least 1 tile needed
 * @param offset - Optional offset to expand the bounding box
 */
export const getBoundingBox = (
    tiles: Coords[],
    offset: Coords = CoordsUtils.zero()
): [Coords, Coords, Coords, Coords] => {
    const { lowX, lowY, highX, highY } = sortByPosition(tiles);

    return [
        { x: lowX - offset.x, y: lowY - offset.y },
        { x: highX + offset.x, y: lowY - offset.y },
        { x: highX + offset.x, y: highY + offset.y },
        { x: lowX - offset.x, y: highY + offset.y }
    ];
};

/**
 * Calculates the size of a bounding box
 */
export const getBoundingBoxSize = (boundingBox: Coords[]): Size => {
    const { lowX, lowY, highX, highY } = sortByPosition(boundingBox);

    return {
        width: highX - lowX + 1,
        height: highY - lowY + 1
    };
};

/**
 * Snaps a position to the nearest grid point
 */
export const snapToGrid = (position: Coords, gridSize: number): Coords => {
    return {
        x: Math.round(position.x / gridSize) * gridSize,
        y: Math.round(position.y / gridSize) * gridSize
    };
};